import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import { authErrorKeys } from 'src/app/util/auth-error.util';

type EnrolStage = 'choose' | 'totp' | 'email' | 'codes';

/**
 * Second-factor enrolment, shared by the login page and the accept-invitation
 * page.
 *
 * It is a child component rather than a route of its own because enrolment is
 * never entered directly: it always continues from an enrolment-stage challenge
 * that the server issued after either a correct password or a redeemed
 * invitation. A route would be reachable without one and would have nothing to
 * do.
 *
 * `completed` fires only once the user has acknowledged their recovery codes.
 * The session token exists from the moment the factor is confirmed, but holding
 * it until acknowledgement is what stops the codes being skipped past -- they
 * are shown exactly once and cannot be reissued, because only hashes are stored.
 */
@Component({
    selector: 'app-mfa-enrol',
    templateUrl: './mfa-enrol.component.html',
    styleUrls: ['../auth-shell.css'],
    standalone: false
})
export class MfaEnrolComponent implements OnInit {
  /** Enrolment-stage challenge from login or invitation acceptance. */
  @Input() challenge: string;
  /** Methods this deployment permits. Server-driven; may be a single entry. */
  @Input() allowedMethods: string[] = ['totp', 'email'];
  /** Emits the session token once the user has acknowledged their recovery codes. */
  @Output() completed = new EventEmitter<string>();

  stage: EnrolStage = 'choose';
  method: 'totp' | 'email';
  busy = false;
  errorKeys: string[] = [];

  qrDataUrl: string;
  secret: string;
  code = '';
  recoveryCodes: string[] = [];

  private sessionToken: string;

  constructor(private auth: AuthService) {}

  ngOnInit() {
    // With only one method permitted there is nothing to choose, so skip a
    // single-option screen and begin it directly.
    if (this.allowedMethods && this.allowedMethods.length === 1) {
      this.begin(this.allowedMethods[0] as 'totp' | 'email');
    }
  }

  allows(method: string): boolean {
    return (this.allowedMethods || []).indexOf(method) !== -1;
  }

  begin(method: 'totp' | 'email') {
    this.busy = true;
    this.errorKeys = [];
    this.auth.enrolBegin(this.challenge, method).subscribe({
      next: (res) => {
        this.busy = false;
        this.method = method;
        this.qrDataUrl = res.qrDataUrl;
        this.secret = res.secret;
        this.code = '';
        this.stage = method;
      },
      error: (err) => { this.busy = false; this.errorKeys = authErrorKeys(err); }
    });
  }

  confirm() {
    if (!this.code || this.busy) return;
    this.busy = true;
    this.errorKeys = [];
    this.auth.enrolConfirm(this.challenge, this.code).subscribe({
      next: (res) => {
        this.busy = false;
        this.sessionToken = res.token;
        this.recoveryCodes = res.recoveryCodes || [];
        /*
         * Recovery codes are issued to administrators only, so most people are
         * enrolled at this point and there is nothing to show. Skipping the
         * stage rather than rendering an empty panel: a screen headed "save
         * these codes" with no codes on it reads as a failure.
         *
         * Their way back in after losing a phone is an administrator clearing
         * the factor, which is a person in the loop rather than a printed
         * bypass everyone has to keep safe.
         */
        if (!this.recoveryCodes.length) {
          this.completed.emit(this.sessionToken);
          return;
        }
        this.stage = 'codes';
      },
      error: (err) => {
        this.busy = false;
        this.code = '';
        this.errorKeys = authErrorKeys(err);
      }
    });
  }

  /** Back to the method choice. Restarting enrolment discards the pending factor server-side. */
  back() {
    this.stage = 'choose';
    this.errorKeys = [];
    this.code = '';
  }

  /** Lets the user save the codes as a file, since they cannot be shown again. */
  downloadCodes() {
    const body = 'RDF Fusion recovery codes\r\n' +
      'Each code works once. Keep them somewhere safe and private.\r\n\r\n' +
      this.recoveryCodes.join('\r\n') + '\r\n';
    const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fusion-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  acknowledge() {
    this.completed.emit(this.sessionToken);
  }
}
